import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  X, AlertCircle, CheckCircle2, Wand2, RefreshCw, Loader2, Edit3, Save, Trash2, Info,
} from 'lucide-react';
import { Button } from './ui/button';

/**
 * Drawer-style modal that shows every "pending" (low-confidence) row for ONE file
 * and lets the user fix things on the spot:
 *   - Inline-edit any cell value
 *   - Accept a single row (with edits) → moves it into the live module table
 *   - Reject a single row → discards it
 *   - Bulk Accept All / Reject All
 *   - "Reprocess file" → re-runs AI extraction on the file (in case the original
 *     pass timed out, returned 0 rows, or was on an old prompt)
 *
 * Built specifically for the user request: "in the situation that rows aren't
 * getting imported properly on upload, I can do something about it."
 */
export const FixRowsDrawer = ({ file, clientId, api, onClose, onChanged }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [edits, setEdits] = useState({}); // {pending_id: {field: value}}

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { data } = await axios.get(`${api}/pending-imports/${clientId}`, {
        params: { file_id: file.id, status: 'pending' },
      });
      setRows(data.items || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load pending rows');
    } finally { setLoading(false); }
  }, [api, clientId, file.id]);

  // Esc to close
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => { load(); }, [load]);

  const setCell = (pid, key, value) => {
    setEdits((e) => ({ ...e, [pid]: { ...(e[pid] || {}), [key]: value } }));
  };

  // Build the row payload to send back: original row_data + any user edits
  const buildRowData = (pi) => {
    const orig = pi.row_data || {};
    const overlay = edits[pi.id] || {};
    // Coerce numeric fields if the user typed numbers
    const merged = { ...orig, ...overlay };
    Object.keys(overlay).forEach((k) => {
      const v = overlay[k];
      if (typeof orig[k] === 'number' && typeof v === 'string' && v.trim() !== '' && !isNaN(Number(v))) {
        merged[k] = Number(v);
      }
    });
    return merged;
  };

  const acceptOne = async (pi) => {
    setBusy(true); setError('');
    try {
      await axios.post(`${api}/pending-imports/${pi.id}/confirm`, { row_data: buildRowData(pi) });
      setRows((r) => r.filter((x) => x.id !== pi.id));
      setEdits((e) => { const n = { ...e }; delete n[pi.id]; return n; });
      onChanged && onChanged();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Accept failed');
    } finally { setBusy(false); }
  };

  const rejectOne = async (pi) => {
    setBusy(true); setError('');
    try {
      await axios.post(`${api}/pending-imports/${pi.id}/reject`);
      setRows((r) => r.filter((x) => x.id !== pi.id));
      setEdits((e) => { const n = { ...e }; delete n[pi.id]; return n; });
      onChanged && onChanged();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Reject failed');
    } finally { setBusy(false); }
  };

  const bulk = async (action) => {
    if (!rows.length) return;
    const verb = action === 'confirm' ? 'accept' : 'reject';
    if (!window.confirm(`${verb === 'accept' ? 'Accept' : 'Reject'} ALL ${rows.length} pending rows for this file?`)) return;
    setBusy(true); setError('');
    try {
      const ids = rows.map((r) => r.id);
      const { data } = await axios.post(`${api}/pending-imports/bulk`, { pending_ids: ids, action });
      if (data.failed > 0) {
        setError(`Processed ${data.processed}, ${data.failed} failed. First error: ${data.errors?.[0]?.error || ''}`);
      }
      await load();
      onChanged && onChanged();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Bulk action failed');
    } finally { setBusy(false); }
  };

  const reprocess = async () => {
    if (!window.confirm('Re-run AI extraction on this file from scratch? New rows will be added; existing pending rows stay.')) return;
    setBusy(true); setError('');
    try {
      await axios.post(`${api}/files/${file.id}/reprocess`);
      await load();
      onChanged && onChanged();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Reprocess failed');
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-stretch justify-end z-50"
      data-testid="fix-rows-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="bg-white w-full max-w-4xl h-full shadow-2xl flex flex-col"
        data-testid="fix-rows-drawer"
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-border flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-primary flex items-center gap-2">
              <Wand2 className="h-5 w-5" /> Fix unimported rows
            </h3>
            <p className="text-xs text-text-muted mt-1 truncate" title={file.original_filename}>
              {file.original_filename} · module <strong>{file.module}</strong> · {rows.length} pending row{rows.length === 1 ? '' : 's'}
            </p>
          </div>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary p-1" data-testid="fix-rows-close" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Action bar */}
        <div className="px-5 py-3 border-b border-border flex flex-wrap gap-2 items-center bg-background-secondary">
          <Button size="sm" onClick={() => bulk('confirm')} disabled={busy || rows.length === 0} data-testid="fix-rows-accept-all">
            <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Accept all
          </Button>
          <Button size="sm" variant="outline" onClick={() => bulk('reject')} disabled={busy || rows.length === 0} data-testid="fix-rows-reject-all">
            <Trash2 className="h-3.5 w-3.5 mr-1" /> Reject all
          </Button>
          <div className="flex-1" />
          <Button size="sm" variant="outline" onClick={reprocess} disabled={busy} data-testid="fix-rows-reprocess">
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${busy ? 'animate-spin' : ''}`} /> Re-extract from file
          </Button>
          <Button size="sm" variant="outline" onClick={load} disabled={loading || busy}>
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Errors */}
        {error && (
          <div className="px-5 py-2 bg-rose-50 border-b border-rose-200 text-xs text-state-error flex items-start gap-2" data-testid="fix-rows-error">
            <AlertCircle className="h-4 w-4 mt-0.5" /> <span>{error}</span>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="fix-rows-list">
          {loading && (
            <div className="flex items-center justify-center py-12 text-text-muted">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading pending rows…
            </div>
          )}

          {!loading && rows.length === 0 && (
            <div className="text-center py-16 text-text-muted">
              <CheckCircle2 className="h-10 w-10 mx-auto text-state-success mb-3" />
              <p className="text-sm font-semibold text-text-primary">All rows accounted for.</p>
              <p className="text-xs mt-1">No pending rows need review for this file.</p>
              <p className="text-xs mt-3">
                If you suspect data is missing entirely, click <strong>Re-extract from file</strong> above to re-run AI on the original upload.
              </p>
            </div>
          )}

          {rows.map((pi) => (
            <PendingRowCard
              key={pi.id}
              pi={pi}
              edits={edits[pi.id] || {}}
              setCell={(k, v) => setCell(pi.id, k, v)}
              onAccept={() => acceptOne(pi)}
              onReject={() => rejectOne(pi)}
              busy={busy}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const PendingRowCard = ({ pi, edits, setCell, onAccept, onReject, busy }) => {
  const row = pi.row_data || {};
  // Hide internal underscore-prefixed fields from the UI
  const visibleKeys = Object.keys(row).filter((k) => !k.startsWith('_'));
  const warnings = pi.warnings || pi.ai_warnings || [];
  const conf = pi.confidence ?? row._confidence ?? null;

  return (
    <div className="border border-border rounded-md p-4 hover:border-primary/30 transition-colors" data-testid={`pending-row-${pi.id}`}>
      {/* Top meta strip */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 text-xs">
          <span className="px-1.5 py-0.5 rounded font-semibold bg-amber-100 text-amber-800">
            <Info className="h-3 w-3 inline mr-0.5" /> Needs review
          </span>
          {conf != null && (
            <span className="text-text-muted">conf <strong>{conf}%</strong></span>
          )}
        </div>
        <div className="flex gap-1">
          <Button size="sm" onClick={onAccept} disabled={busy} className="bg-state-success hover:bg-emerald-700 text-white" data-testid={`pending-accept-${pi.id}`}>
            <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Accept
          </Button>
          <Button size="sm" variant="outline" onClick={onReject} disabled={busy} className="text-state-error border-state-error hover:bg-rose-50" data-testid={`pending-reject-${pi.id}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Warnings */}
      {warnings.length > 0 && (
        <ul className="mb-3 text-[11px] bg-amber-50 border border-amber-200 rounded p-2 text-amber-900 list-disc list-inside">
          {warnings.slice(0, 4).map((w, i) => <li key={i}>{w}</li>)}
        </ul>
      )}

      {/* Editable fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
        {visibleKeys.map((k) => {
          const orig = row[k];
          const value = edits[k] !== undefined ? edits[k] : (orig ?? '');
          const dirty = edits[k] !== undefined && edits[k] !== orig;
          return (
            <div key={k}>
              <label className="block text-[10px] uppercase tracking-wider text-text-secondary font-semibold mb-1">
                {k} {dirty && <span className="text-state-warning normal-case tracking-normal">· edited</span>}
              </label>
              <input
                value={value === null ? '' : String(value)}
                onChange={(e) => setCell(k, e.target.value)}
                className={`w-full border rounded px-2 py-1 text-sm font-mono ${dirty ? 'border-state-warning bg-amber-50' : 'border-border'}`}
                data-testid={`pending-field-${pi.id}-${k}`}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FixRowsDrawer;
